"use client";

import Link, { useLinkStatus } from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  FileText,
  Film,
  FileUp,
  LayoutGrid,
  Layers,
  Loader2,
  MapPin,
  Play,
  Users,
  Package,
  Wand2,
} from "lucide-react";

interface ProjectSidebarProps {
  projectId: string;
}

/**
 * 导航等待指示器。
 *
 * 🔴 2026-09-17「切换菜单跳转非常慢」优化配套：改造前点菜单**没有任何反馈** ——
 * 数据要等 300ms+ 才到，期间界面纹丝不动，用户以为「点了没反应」而反复点击。
 * 用 Next 16 内置的 `useLinkStatus()`（**必须在 `<Link>` 的子组件内调用**）
 * 在目标条目上显示转圈，让等待可感知。
 *
 * 只做态提示，不做假进度条 —— 假进度会在快速切换时闪来闪去，比没有更糟。
 */
function NavPendingDot({ active }: { active: boolean }) {
  const { pending } = useLinkStatus();
  if (!pending) return null;
  return (
    <Loader2
      className={cn(
        "ml-auto h-3.5 w-3.5 flex-shrink-0 animate-spin",
        active ? "text-primary" : "text-[var(--text-muted)]"
      )}
      aria-hidden
    />
  );
}

/**
 * 项目工作台左侧栏（项目层，所有项目页共享）。
 *
 * 分两级，且**互斥渲染**：
 *  1. **项目导航** —— 分集管理 / 角色 / 导入记录 / 提示词管理。仅在非详情页出现。
 *     注意：`project/[id]/script|storyboard|preview` 是 legacy 重定向页（redirect 到 /episodes），
 *     所以**不列入**导航，否则点一下就跳走。
 *  2. **本集流程** —— 仅当路径处于 `/episodes/{episodeId}/...` 时出现：
 *     返回分集列表 + 1 剧本 / 2 角色 / 3 场景 / 4 道具（项目级页，带 ?episodeId 预选）/ 5 分镜 / 6 预览。
 *     ⚠️ 此时**整个第 1 级不渲染**：两级不是同一层级的东西（一个是项目域，一个是本集域），
 *     排进同一个纵向列表靠小标题分隔，看上去就是「层级重叠」——用户明确反馈过。
 *     返回项目域的出口只留「返回分集列表」。
 *
 * ⚠️ 历史坑：这一级侧栏以前**只挂在剧集详情布局**（`episodes/[episodeId]/layout.tsx`）里，
 * 于是从详情页返回分集管理页时左侧栏会整个消失（项目层 `project/[id]/layout.tsx` 没有侧栏）。
 * 而 `project/[id]/loading.tsx` 的骨架注释早写明「侧栏 + 内容区，与项目页布局形态一致」——
 * 即设计意图本就是项目层统一侧栏。现在按该意图实现：项目层渲染；详情页**只**渲染第 2 级。
 */
export function ProjectSidebar({ projectId }: ProjectSidebarProps) {
  const t = useTranslations("project");
  const tProp = useTranslations("prop");
  const tc = useTranslations("common");
  const tEpisode = useTranslations("episode");
  const tAsset = useTranslations("assets");
  const locale = useLocale();
  const pathname = usePathname();

  const base = `/${locale}/project/${projectId}`;

  // `/episodes/{episodeId}/...` → episodeId；`/episodes` 本身不算（否则 "episodes" 会被当成集 id）
  const segs = pathname.split("/").filter(Boolean);
  const epIdx = segs.lastIndexOf("episodes");
  const episodeId =
    epIdx >= 0 && segs.length > epIdx + 1 ? segs[epIdx + 1] : null;
  const stepBase = episodeId ? `${base}/episodes/${episodeId}` : null;

  /**
   * 项目导航（第 1 级）：分集管理 / 角色 / 素材库 / 导入记录 / 提示词管理。
   *   🔴 2026-10-18：去掉「道具」—— 道具不再是独立入口，已按
   *   `prop_states.holder / scene_id` 融入角色页与场景页（见 RelatedProps）。
   *
   * ⚠️ 只在**非详情页**渲染。进入 `/episodes/{episodeId}/...` 后，侧栏整体让位给
   * 「本集流程」（第 2 级）—— 两者**不是同一层级的东西**，排进同一个纵向列表里
   * 只会看起来像一层（用户明确反馈「层级重叠」）。所以这里做成**互斥**：
   *   详情页 → 只渲染 返回分集列表 + 1–5；
   *   其它页 → 只渲染本导航。
   */
  const navItems = [
    { href: `${base}/episodes`, icon: Layers, label: tEpisode("title") },
    { href: `${base}/characters`, icon: Users, label: t("characters") },
    { href: `${base}/props`, icon: Package, label: tProp("title") },
    { href: `${base}/assets`, icon: LayoutGrid, label: tAsset("title") },
    { href: `${base}/import`, icon: FileUp, label: tEpisode("importRecord") },
    { href: `${base}/prompts`, icon: Wand2, label: tc("promptTemplates") },
  ];

  /**
   * 🔴 2026-10-18（老板：不需要独立的道具入口，把道具融入角色与场景）：
   * 原来这里是 6 步，第 4 步是「道具」。道具一旦独立成步，用户在角色/场景页
   * **看不到自己手里的东西在哪、什么状态**，得跳到另一个页面去对账。
   * 现在把道具**挂在它本来就属于的地方**（`prop_states.holder` → 角色页、
   * `prop_states.scene_id` → 场景页，各自显示最新状态），本流程只保留五步。
   * 分集级 `/episodes/{id}/props` 页面仍然存在（深链与批量管理仍可用），只是不再占导航位。
   */
  const steps: { key: string; num: number; icon: LucideIcon; label: string; href?: string }[] = [
    { key: "script", num: 1, icon: FileText, label: t("script") },
    { key: "characters", num: 2, icon: Users, label: t("characters") },
    { key: "scenes", num: 3, icon: MapPin, label: t("scenes") },
    {
      key: "props",
      num: 4,
      icon: Package,
      label: tProp("title"),
      href: `${stepBase}/props`,
    },
    { key: "storyboard", num: 5, icon: Film, label: t("storyboard") },
    { key: "preview", num: 6, icon: Play, label: t("preview") },
  ];

  /** 前缀匹配：/episodes 要能覆盖 /episodes/{episodeId}/... （详情页仍属分集管理） */
  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const itemCls = (active: boolean) =>
    cn(
      "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
      active
        ? "bg-primary/8 text-primary"
        : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
    );

  const badgeCls = (active: boolean, square = false) =>
    cn(
      "flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-200",
      square && "text-xs font-bold",
      active
        ? "bg-primary text-white shadow-sm shadow-primary/25"
        : "bg-[var(--surface)] text-[var(--text-muted)] group-hover:bg-primary/10 group-hover:text-primary"
    );

  return (
    <>
      {/* 桌面侧栏 —— 相对顶栏（h-14）固定，不随右侧内容滚动。
          类名刻意沿用原 ProjectNav 的那一套（这套 arbitrary value 类在本 dev 环境
          已被 Tailwind 编译过）：这个环境里**首次出现**的任意值类不会立即生效。 */}
      <nav className="hidden w-60 flex-shrink-0 border-r border-[var(--border-subtle)] bg-white lg:block self-start sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
        <div className="flex flex-col gap-1 p-3 pt-4">
          {stepBase ? (
            <>
              {/* 第 2 级：本集流程 —— 详情页只呈现这一层（项目导航让位） */}
              <Link
                href={`${base}/episodes`}
                className="mb-1 flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <ArrowLeft className="h-3 w-3" />
                {tEpisode("backToList")}
                <NavPendingDot active={false} />
              </Link>
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-muted)]">
                {t("navEpisodeSteps")}
              </p>
              {steps.map((step) => {
                // 有 href 覆盖的（道具）走覆盖值；其余按 /episodes/{episodeId}/{key} 拼
                const href = step.href ?? `${stepBase}/${step.key}`;
                const active = pathname === href.split("?")[0];
                const Icon = step.icon;
                return (
                  <Link key={step.key} href={href} className={itemCls(active)}>
                    {active && (
                      <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
                    )}
                    <span className={badgeCls(active, true)}>{step.num}</span>
                    <Icon className="h-4 w-4" />
                    <span>{step.label}</span>
                    <NavPendingDot active={active} />
                  </Link>
                );
              })}
            </>
          ) : (
            <>
              {/* 第 1 级：项目导航 —— 只在非详情页出现 */}
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-muted)]">
                {t("navProject")}
              </p>
              {navItems.map((item) => {
                const active = isActive(item.href);
                const Icon = item.icon;
                return (
                  <Link key={item.href} href={item.href} className={itemCls(active)}>
                    {active && (
                      <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
                    )}
                    <span className={badgeCls(active)}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>{item.label}</span>
                    <NavPendingDot active={active} />
                  </Link>
                );
              })}
            </>
          )}
        </div>
      </nav>

      {/* 移动端底部 tab —— 仅剧集详情页（项目层在窄屏靠各页顶部按钮导航） */}
      {stepBase && (
        // data-bottom-nav：后台任务浮窗要量它的高度来抬高自己，别被这条 tab 栏挡住（2026-10-03）
        <nav
          data-bottom-nav
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--border-subtle)] bg-white/95 backdrop-blur-md lg:hidden"
        >
          <div className="flex items-center justify-around py-1.5">
            {steps.map((step) => {
              const href = step.href ?? `${stepBase}/${step.key}`;
              const active = pathname === href.split("?")[0];
              const Icon = step.icon;
              return (
                <Link
                  key={step.key}
                  href={href}
                  className={cn(
                    "flex flex-col items-center gap-0.5 px-4 py-1.5 text-[11px] font-medium transition-colors",
                    active
                      ? "text-primary"
                      : "text-[var(--text-muted)] active:text-[var(--text-secondary)]"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-lg transition-all",
                      active && "bg-primary/8"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <span>{step.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
}
